import { L10nTranslationService } from 'angular-l10n';
import { DashboardLayoutEdit, DashboardLayoutEditResult } from '../layout/dashboard-layouts';
import { MAX_SAVED_VIEWS } from '../views/dashboard-view';
import { DashboardViewOutcome, DashboardViewRejection } from '../views/dashboard-views';

/**
 * What a screen reader hears after an edit to the draft, or null for edits the user sees happen (drags,
 * panel controls). `title` names the edited widget or widget type.
 */
export function describeEdit(
  edit: DashboardLayoutEdit,
  result: DashboardLayoutEditResult,
  title: string,
  translation: L10nTranslationService,
): string | null {
  if ('reason' in result) {
    return result.reason === 'invalid-settings'
      ? translation.translate('Dashboard.Announce.InvalidSettings', { title })
      : null;
  }

  const widget = result.widget;
  const place = widget && { title, column: widget.x + 1, row: widget.y + 1, width: widget.w, height: widget.h };

  switch (edit.kind) {
    case 'add':
      return place ? translation.translate('Dashboard.Announce.Added', place) : null;
    case 'remove':
    case 'removeType':
      return translation.translate('Dashboard.Announce.Removed', { title });
    case 'arrange':
      if (!place || !edit.keyboard) {
        return null;
      }

      return translation.translate(
        edit.keyboard.kind === 'moveBy' ? 'Dashboard.Announce.Moved' : 'Dashboard.Announce.Resized',
        place,
      );
    default:
      return null;
  }
}

/** Failures are shown, not only announced */
export function isViewFailure(outcome: DashboardViewOutcome): boolean {
  switch (outcome.kind) {
    case 'shown':
      return !outcome.remembered;
    case 'save-failed':
    case 'delete-failed':
    case 'reset-failed':
    case 'rejected':
      return true;
    default:
      return false;
  }
}

/** Null for refusals the panel already prevents, such as a busy store or an invalid name */
export function describeViewOutcome(outcome: DashboardViewOutcome, translation: L10nTranslationService): string | null {
  switch (outcome.kind) {
    case 'shown':
      return translation.translate(
        outcome.remembered ? 'Dashboard.Announce.Showing' : 'Dashboard.Announce.ShowingNotRemembered',
        { name: outcome.name },
      );
    case 'saved':
      return translation.translate('Dashboard.Announce.Saved', { name: outcome.view.name });
    case 'save-failed':
      return translation.translate('Dashboard.Announce.SaveFailed');
    case 'deleted':
      return translation.translate('Dashboard.Announce.Deleted', {
        name: outcome.name,
        defaultName: translation.translate('Dashboard.Views.Default'),
      });
    case 'delete-failed':
      return translation.translate('Dashboard.Announce.DeleteFailed', { name: outcome.name });
    case 'reset':
      return translation.translate('Dashboard.Announce.Reset');
    case 'reset-failed':
      return translation.translate('Dashboard.Announce.ResetFailed');
    case 'rejected':
      return describeRejection(outcome.reason, translation);
  }
}

function describeRejection(reason: DashboardViewRejection, translation: L10nTranslationService): string | null {
  switch (reason) {
    case 'read-only':
      return translation.translate('Dashboard.Views.Unreadable');
    case 'missing':
      return translation.translate('Dashboard.Views.Missing');
    case 'limit':
      return translation.translate('Dashboard.Panel.AtLimit', { count: MAX_SAVED_VIEWS });
    case 'busy':
    case 'invalid-name':
      return null;
  }
}
